import type { dateRangeDto, EchartsConfig, ILaneConfig, LaneConfig, ZoomSliderMode } from 'commons';
import type { DataZoomComponentOption, GridComponentOption, SeriesOption, XAXisComponentOption, YAXisComponentOption } from 'echarts';
import type { TitleOption } from 'echarts/types/dist/shared';
import type { EditmodeStateModel } from '../../../app/databrowser/lane-edit-mode-panel/editmode.types';
import type { TestPhaseItem } from '../../../app/states/test-phase';
import type { CommentModel } from '../../../services/api/models/CommentModel';
import type { KPI_Table } from '../../dtis-multilane-viewer/KPI_Table';
import type { DtisZoomEvent } from '../zoomEvent';

/**
 * Input state for chart configuration generation
 */
export interface ChartConfigState {
  readonly kpiTable: KPI_Table;
  readonly laneConfig: LaneConfig[];
  readonly zoomSliderMode: ZoomSliderMode;
  readonly comments: CommentModel[];
  readonly testPhasesVisible?: boolean;
  readonly testPhases?: TestPhaseItem[];
  readonly commentsVisible?: boolean;
}

/**
 * Edit mode context for chart configuration
 */
export interface ChartEditModeContext {
  readonly currentYAxisPanel?: string | null;
  readonly highlightedComment?: string | null;
}

/**
 * Complete input for chart configuration generation
 */
export interface ChartConfigInput {
  readonly state: ChartConfigState;
  readonly zoom: DtisZoomEvent[];
  readonly dateRange: dateRangeDto;
  readonly editmode: Partial<EditmodeStateModel>;
  readonly gridHeight?: number;
  readonly gridConfig?: EchartsConfig;
}

/**
 * Output configuration for ECharts
 */
export interface EchartsChartConfig {
  readonly grid: GridComponentOption[];
  readonly xAxis: XAXisComponentOption[];
  readonly yAxis: YAXisComponentOption[];
  readonly title: TitleOption | TitleOption[];
  readonly dataZoom: DataZoomComponentOption[];
  readonly series: SeriesOption[];
  readonly tooltip: {
    readonly show: boolean;
    readonly formatter: (params: unknown) => string;
  };
}

/**
 * Series data point tuple
 * [x-value, y-value, unit-or-metadata]
 */
export type SeriesDataPoint = [Date | number | string, number | null, string | Record<string, unknown> | null | undefined];

/**
 * Configuration for grid layout
 */
export interface GridLayoutConfig {
  readonly gridGapBase?: number;
  readonly gridLeftPadding?: number;
  readonly gridRightPadding?: number;
  readonly gridZoomLevelOffset?: number;
  readonly gridLaneOffsetValue?: number;
  readonly gridWidthPercentage?: number;
}

/**
 * Configuration for Y-axis styling
 */
export interface YAxisStyleConfig {
  readonly yAxisOffsetBase?: number;
  readonly yAxisSplitNumber?: number;
  readonly yAxisBoundaryGap?: number;
  readonly yAxisNameGap?: number;
}

/**
 * Configuration for title styling
 */
export interface TitleStyleConfig {
  readonly titleOffset?: number;
  readonly titleFontSize?: number;
  readonly titleLeft?: number;
}

/**
 * Combined chart styling configuration
 */
export interface ChartStyleConfig extends GridLayoutConfig, YAxisStyleConfig, TitleStyleConfig {
  readonly gridHeight?: number;
}

/**
 * Context for building series
 */
export interface SeriesBuildContext {
  readonly laneConfig: LaneConfig | ILaneConfig;
  readonly laneIndex: number;
  readonly xAxis: XAXisComponentOption[];
  readonly yAxis: YAXisComponentOption[];
  readonly comments: CommentModel[];
  readonly highlightedComment?: string | null;
}

/**
 * Context for building data zoom components
 */
export interface DataZoomBuildContext {
  readonly grid: GridComponentOption[];
  readonly xAxis: XAXisComponentOption[];
  readonly yAxis: YAXisComponentOption[];
  readonly zoomSliderMode: ZoomSliderMode;
  readonly zoom: DtisZoomEvent[];
}

/**
 * Chart constants
 */
export const CHART_CONSTANTS = {
  DECIMAL_PLACE: 4,
  DEFAULT_GRID_HEIGHT: 180,
  GRID_BASE_HEIGHT: 150,
  GRID_GAP_BASE: 75,
  GRID_GAP_LANE_ZOOM: 150,
  GRID_PADDING_TOP: 180,
  ZOOM_SLIDER_HEIGHT: 40,
  ZOOM_LEVEL_OFFSET: 55,
  LANE_OFFSET_VALUE: 10,
  DEFAULT_GRID_LEFT_PADDING: 80,
  DEFAULT_GRID_RIGHT_PADDING: 30,
  DEFAULT_GRID_WIDTH_PERCENTAGE: 85,
} as const;

/**
 * Translucent colors for chart elements
 */
export const CHART_COLORS = {
  TRANSLUCENT_GREEN: 'rgba(0, 137, 61, 0.82)',
  COMMENTARY_BLUE: '#1C98B5',
  SCHAEFFLER_GREEN: 'rgba(0, 137, 61, 1)',
} as const;
